import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { DollarSign, AlertTriangle, AlertCircle, CheckCircle, XCircle, Eye, Lock, Key, Copy, X, Save } from 'lucide-react';
import { Switch } from '../ui/switch';
import { toast } from 'sonner';
import { getBackendUrl } from '../../utils/api';
import EvolutionAPIConfigSection from './EvolutionAPIConfigSection';
import WhatsAppAIConfigSection from './WhatsAppAIConfigSection';

const BACKEND_URL = getBackendUrl();
const API = `${BACKEND_URL}/api`;

// ========== LOGS DE ERROS DA IA ==========
export const MasterSystemSettingsContent = () => {
  const [settings, setSettings] = useState({
    mp_access_token_global: '',
    mp_public_key_global: '',
    subscription_price: 99.00,
    menu_base_url: ''  // URL base para links do cardápio nas mensagens de saudação
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [configured, setConfigured] = useState(false);
  const [showTokens, setShowTokens] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [maskedData, setMaskedData] = useState({
    token: '',
    key: ''
  });

  // Estados para Instruções Globais da IA (Master)
  const [globalAiInstructions, setGlobalAiInstructions] = useState('');
  const [globalAiInstructionsEnabled, setGlobalAiInstructionsEnabled] = useState(true);
  const [savingAiInstructions, setSavingAiInstructions] = useState(false);
  const [aiInstructionsSuccess, setAiInstructionsSuccess] = useState('');
  const [aiInstructionsError, setAiInstructionsError] = useState('');

  // Estados para Deepgram (Transcrição de Áudio)
  const [deepgramConfig, setDeepgramConfig] = useState({
    enabled: false,
    deepgram_api_key: ''
  });
  const [deepgramConfigured, setDeepgramConfigured] = useState(false);
  const [deepgramMaskedKey, setDeepgramMaskedKey] = useState('');
  const [savingDeepgram, setSavingDeepgram] = useState(false);
  const [deepgramSuccess, setDeepgramSuccess] = useState('');
  const [deepgramError, setDeepgramError] = useState('');

  // Estados para alteração de senha do master
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [changingPassword, setChangingPassword] = useState(false);
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [showPasswords, setShowPasswords] = useState(false);

  // Estados para sincronização de pagamentos
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const [diagnostico, setDiagnostico] = useState(null);
  const [loadingDiagnostico, setLoadingDiagnostico] = useState(false);

  useEffect(() => {
    loadSettings();
    loadDeepgramConfig();
  }, []);

  const loadDeepgramConfig = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/master/deepgram-config`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.configured) {
        setDeepgramConfigured(true);
        setDeepgramMaskedKey(response.data.deepgram_api_key_masked || '');
        setDeepgramConfig(prev => ({
          ...prev,
          enabled: response.data.enabled
        }));
      }
    } catch (err) {
      console.error('Error loading Deepgram config:', err);
    }
  };

  const handleSaveDeepgram = async () => {
    try {
      setSavingDeepgram(true);
      setDeepgramError('');
      setDeepgramSuccess('');

      if (!deepgramConfig.deepgram_api_key && !deepgramConfigured) {
        setDeepgramError('API Key do Deepgram é obrigatória');
        return;
      }

      const token = localStorage.getItem('token');
      
      const updateData = {
        enabled: deepgramConfig.enabled
      };

      if (deepgramConfig.deepgram_api_key) {
        updateData.deepgram_api_key = deepgramConfig.deepgram_api_key;
      }

      await axios.put(`${API}/master/deepgram-config`, updateData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setDeepgramSuccess('Configurações do Deepgram salvas com sucesso!');
      setDeepgramConfig(prev => ({
        ...prev,
        deepgram_api_key: ''
      }));
      
      setTimeout(() => {
        loadDeepgramConfig();
        setDeepgramSuccess('');
      }, 2000);

    } catch (err) {
      console.error('Error saving Deepgram config:', err);
      setDeepgramError(err.response?.data?.detail || 'Erro ao salvar configurações');
    } finally {
      setSavingDeepgram(false);
    }
  };

  const loadSettings = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/master/system-settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.configured) {
        setConfigured(true);
        setMaskedData({
          token: response.data.mp_access_token_global_masked,
          key: response.data.mp_public_key_global_masked
        });
      }
      
      setSettings({
        mp_access_token_global: '',
        mp_public_key_global: '',
        subscription_price: response.data.subscription_price || 99.00,
        menu_base_url: response.data.menu_base_url || ''
      });
      
      // Carregar instruções globais da IA
      setGlobalAiInstructions(response.data.global_ai_instructions || '');
      setGlobalAiInstructionsEnabled(response.data.global_ai_instructions_enabled !== false);
      
    } catch (err) {
      console.error('Error loading settings:', err);
      setError('Erro ao carregar configurações');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');
      setSuccess('');

      if (!settings.mp_access_token_global && !configured) {
        setError('Access Token é obrigatório');
        return;
      }

      if (settings.subscription_price <= 0) {
        setError('Preço da assinatura deve ser maior que zero');
        return;
      }

      const token = localStorage.getItem('token');
      
      // Only send fields that have values
      const updateData = {
        subscription_price: settings.subscription_price
      };

      if (settings.mp_access_token_global) {
        updateData.mp_access_token_global = settings.mp_access_token_global;
      }

      if (settings.mp_public_key_global) {
        updateData.mp_public_key_global = settings.mp_public_key_global;
      }

      // Sempre enviar menu_base_url (pode ser string vazia para resetar)
      updateData.menu_base_url = settings.menu_base_url || '';

      // For updates, require at least the token
      if (!configured && !updateData.mp_access_token_global) {
        setError('Access Token do Mercado Pago é obrigatório');
        return;
      }

      await axios.put(`${API}/master/system-settings`, updateData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setSuccess('Configurações salvas com sucesso!');
      setSettings({
        mp_access_token_global: '',
        mp_public_key_global: '',
        subscription_price: settings.subscription_price,
        menu_base_url: settings.menu_base_url
      });
      
      // Reload to get masked values
      setTimeout(() => {
        loadSettings();
        setSuccess('');
      }, 2000);

    } catch (err) {
      console.error('Error saving settings:', err);
      setError(err.response?.data?.detail || 'Erro ao salvar configurações');
    } finally {
      setSaving(false);
    }
  };

  // Função para salvar instruções globais da IA
  const handleSaveAiInstructions = async () => {
    try {
      setSavingAiInstructions(true);
      setAiInstructionsError('');
      setAiInstructionsSuccess('');

      const token = localStorage.getItem('token');
      
      await axios.put(`${API}/master/system-settings`, {
        global_ai_instructions: globalAiInstructions,
        global_ai_instructions_enabled: globalAiInstructionsEnabled
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setAiInstructionsSuccess('Instruções globais da IA salvas com sucesso!');
      
      setTimeout(() => {
        setAiInstructionsSuccess('');
      }, 3000);

    } catch (err) {
      console.error('Error saving AI instructions:', err);
      setAiInstructionsError(err.response?.data?.detail || 'Erro ao salvar instruções da IA');
    } finally {
      setSavingAiInstructions(false);
    }
  };

  // Função para carregar diagnóstico de webhooks
  const loadDiagnostico = async () => {
    try {
      setLoadingDiagnostico(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/mercadopago/diagnostico-webhook`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDiagnostico(response.data);
    } catch (err) {
      console.error('Erro ao carregar diagnóstico:', err);
      toast.error('Erro ao carregar diagnóstico');
    } finally {
      setLoadingDiagnostico(false);
    }
  };

  // Função para sincronizar pagamentos pendentes
  const handleSincronizarPagamentos = async () => {
    try {
      setSyncing(true);
      setSyncResult(null);
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/mercadopago/sincronizar-pagamentos`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setSyncResult(response.data);
      
      if (response.data.aprovados > 0) {
        toast.success(`${response.data.aprovados} assinatura(s) renovada(s)!`);
      } else {
        toast.info('Nenhum pagamento aprovado encontrado');
      }
    } catch (err) {
      console.error('Erro ao sincronizar:', err);
      toast.error(err.response?.data?.detail || 'Erro ao sincronizar pagamentos');
    } finally {
      setSyncing(false);
    }
  };

  // Função para alterar senha do master
  const handleChangePassword = async () => {
    try {
      setChangingPassword(true);
      setPasswordError('');
      setPasswordSuccess('');

      // Validações
      if (!passwordData.current_password) {
        setPasswordError('Digite sua senha atual');
        return;
      }

      if (!passwordData.new_password) {
        setPasswordError('Digite a nova senha');
        return;
      }

      if (passwordData.new_password.length < 6) {
        setPasswordError('A nova senha deve ter pelo menos 6 caracteres');
        return;
      }

      if (passwordData.new_password !== passwordData.confirm_password) {
        setPasswordError('A nova senha e a confirmação não coincidem');
        return;
      }

      const token = localStorage.getItem('token');
      await axios.post(`${API}/master/change-password`, passwordData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setPasswordSuccess('Senha alterada com sucesso!');
      setPasswordData({
        current_password: '',
        new_password: '',
        confirm_password: ''
      });

      // Limpar mensagem de sucesso após 3 segundos
      setTimeout(() => {
        setPasswordSuccess('');
      }, 3000);

    } catch (err) {
      console.error('Error changing password:', err);
      setPasswordError(err.response?.data?.detail || 'Erro ao alterar senha');
    } finally {
      setChangingPassword(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-400">Carregando configurações...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white">Configurações do Sistema</h1>
          <p className="text-gray-400 mt-1">Configure credenciais globais para Evolution API e Mercado Pago</p>
        </div>
      </div>

      {/* Seção de Alteração de Senha do Master */}
      <div className="glass-card">
        <div className="p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
              <Lock className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">Alterar Senha do Administrador</h2>
              <p className="text-gray-400 text-sm">Atualize sua senha de acesso ao painel master</p>
            </div>
          </div>

          {/* Mensagens de sucesso/erro */}
          {passwordSuccess && (
            <div className="mb-4 p-4 bg-green-500/20 border border-green-500/30 rounded-lg">
              <p className="text-green-400 flex items-center gap-2">
                <CheckCircle className="w-5 h-5" />
                {passwordSuccess}
              </p>
            </div>
          )}
          
          {passwordError && (
            <div className="mb-4 p-4 bg-red-500/20 border border-red-500/30 rounded-lg">
              <p className="text-red-400 flex items-center gap-2">
                <AlertCircle className="w-5 h-5" />
                {passwordError}
              </p>
            </div>
          )}

          <div className="space-y-4">
            {/* Senha Atual */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Senha Atual *
              </label>
              <div className="relative">
                <input
                  type={showPasswords ? "text" : "password"}
                  value={passwordData.current_password}
                  onChange={(e) => setPasswordData({...passwordData, current_password: e.target.value})}
                  placeholder="Digite sua senha atual"
                  className="w-full bg-gray-800/50 border border-gray-700 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>
            </div>

            {/* Nova Senha */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Nova Senha *
              </label>
              <div className="relative">
                <input
                  type={showPasswords ? "text" : "password"}
                  value={passwordData.new_password}
                  onChange={(e) => setPasswordData({...passwordData, new_password: e.target.value})}
                  placeholder="Digite a nova senha (mínimo 6 caracteres)"
                  className="w-full bg-gray-800/50 border border-gray-700 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>
            </div>

            {/* Confirmar Nova Senha */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Confirmar Nova Senha *
              </label>
              <div className="relative">
                <input
                  type={showPasswords ? "text" : "password"}
                  value={passwordData.confirm_password}
                  onChange={(e) => setPasswordData({...passwordData, confirm_password: e.target.value})}
                  placeholder="Confirme a nova senha"
                  className="w-full bg-gray-800/50 border border-gray-700 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>
            </div>

            {/* Toggle para mostrar/ocultar senhas */}
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="showPasswords"
                checked={showPasswords}
                onChange={(e) => setShowPasswords(e.target.checked)}
                className="rounded bg-gray-800 border-gray-700 text-purple-500 focus:ring-purple-500"
              />
              <label htmlFor="showPasswords" className="text-sm text-gray-400 cursor-pointer">
                Mostrar senhas
              </label>
            </div>

            {/* Botão de Salvar */}
            <div className="pt-4">
              <button
                onClick={handleChangePassword}
                disabled={changingPassword}
                className="w-full sm:w-auto px-6 py-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white font-semibold rounded-lg hover:from-purple-600 hover:to-pink-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2"
              >
                {changingPassword ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                    Alterando...
                  </>
                ) : (
                  <>
                    <Lock className="w-5 h-5" />
                    Alterar Senha
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Divider */}
      <div className="border-t border-gray-700 my-8"></div>

      {/* Seção de Instruções Globais da IA */}
      <div className="glass-card">
        <div className="p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center">
              <span className="text-2xl">🤖</span>
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">Instruções Globais da IA</h2>
              <p className="text-gray-400 text-sm">Regras obrigatórias que valem para TODAS as lojas</p>
            </div>
            {globalAiInstructionsEnabled && globalAiInstructions && (
              <span className="ml-auto px-3 py-1 bg-green-500/20 text-green-400 text-sm rounded-full">
                ✓ Ativo
              </span>
            )}
          </div>

          {/* Mensagens de sucesso/erro */}
          {aiInstructionsSuccess && (
            <div className="mb-4 p-4 bg-green-500/20 border border-green-500/30 rounded-lg">
              <p className="text-green-400 flex items-center gap-2">
                <CheckCircle className="w-5 h-5" />
                {aiInstructionsSuccess}
              </p>
            </div>
          )}
          
          {aiInstructionsError && (
            <div className="mb-4 p-4 bg-red-500/20 border border-red-500/30 rounded-lg">
              <p className="text-red-400 flex items-center gap-2">
                <AlertCircle className="w-5 h-5" />
                {aiInstructionsError}
              </p>
            </div>
          )}

          {/* Info Box */}
          <div className="mb-6 p-4 bg-indigo-500/10 border border-indigo-500/20 rounded-lg">
            <p className="text-indigo-300 text-sm">
              <strong>🎯 Como funciona:</strong> As instruções definidas aqui são aplicadas automaticamente 
              a TODAS as IAs de todas as lojas, antes da personalização de cada lojista.
            </p>
            <p className="text-indigo-300/80 text-xs mt-2">
              <strong>📋 Hierarquia:</strong> Instruções Globais (Master) → Personalização do Lojista → Cardápio da Loja
            </p>
          </div>

          {/* Exemplo de uso */}
          <div className="mb-6 p-4 bg-gray-800/50 border border-gray-700 rounded-lg">
            <p className="text-gray-400 text-sm mb-2"><strong>💡 Exemplos de regras:</strong></p>
            <ul className="text-gray-500 text-xs space-y-1 list-disc list-inside">
              <li>NUNCA confirme produtos que não estão listados no cardápio</li>
              <li>Se cliente perguntar sobre produto inexistente, diga que não trabalha e sugira alternativas</li>
              <li>NUNCA negocie preços - os valores são fixos</li>
              <li>Sempre confirme o pedido completo antes de finalizar</li>
            </ul>
          </div>

          <div className="space-y-4">
            {/* Toggle Ativo/Inativo */}
            <div className="flex items-center justify-between p-4 bg-gray-800/50 rounded-lg border border-gray-700">
              <div>
                <p className="text-white font-medium">Instruções Globais Ativas</p>
                <p className="text-gray-400 text-sm">
                  {globalAiInstructionsEnabled 
                    ? 'As regras estão sendo aplicadas em todas as lojas' 
                    : 'As regras estão desativadas temporariamente'}
                </p>
              </div>
              <button
                onClick={() => setGlobalAiInstructionsEnabled(!globalAiInstructionsEnabled)}
                className={`relative w-14 h-7 rounded-full transition-colors ${
                  globalAiInstructionsEnabled ? 'bg-green-500' : 'bg-gray-600'
                }`}
              >
                <span
                  className={`absolute top-1 w-5 h-5 bg-white rounded-full transition-transform ${
                    globalAiInstructionsEnabled ? 'translate-x-8' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>

            {/* Textarea para instruções */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Instruções para a IA
              </label>
              <textarea
                value={globalAiInstructions}
                onChange={(e) => setGlobalAiInstructions(e.target.value)}
                placeholder="Digite as regras que todas as IAs devem seguir...&#10;&#10;Exemplo:&#10;1. NUNCA confirme produtos que não estão no cardápio&#10;2. Se cliente perguntar sobre produto inexistente, sugira alternativas&#10;3. Sempre seja educado e profissional"
                rows={10}
                className="w-full bg-gray-800/50 border border-gray-700 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent font-mono text-sm"
              />
              <p className="text-gray-500 text-xs mt-2">
                {globalAiInstructions.length} caracteres
              </p>
            </div>

            {/* Botão Salvar */}
            <div className="pt-4">
              <button
                onClick={handleSaveAiInstructions}
                disabled={savingAiInstructions}
                className="w-full sm:w-auto px-6 py-3 bg-gradient-to-r from-indigo-500 to-purple-500 text-white font-semibold rounded-lg hover:from-indigo-600 hover:to-purple-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2"
              >
                {savingAiInstructions ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                    Salvando...
                  </>
                ) : (
                  <>
                    <Save className="w-5 h-5" />
                    Salvar Instruções Globais
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Divider */}
      <div className="border-t border-gray-700 my-8"></div>

      {/* Seção Deepgram - Transcrição de Áudio */}
      <div className="glass-card">
        <div className="p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-teal-500 to-cyan-500 flex items-center justify-center">
              <span className="text-2xl">🎤</span>
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">Deepgram - Transcrição de Áudio</h2>
              <p className="text-gray-400 text-sm">Configure para que a IA responda mensagens de áudio no WhatsApp</p>
            </div>
            {deepgramConfigured && deepgramConfig.enabled && (
              <span className="ml-auto px-3 py-1 bg-green-500/20 text-green-400 text-sm rounded-full">
                ✓ Ativo
              </span>
            )}
          </div>

          {/* Mensagens de sucesso/erro */}
          {deepgramSuccess && (
            <div className="mb-4 p-4 bg-green-500/20 border border-green-500/30 rounded-lg">
              <p className="text-green-400 flex items-center gap-2">
                <CheckCircle className="w-5 h-5" />
                {deepgramSuccess}
              </p>
            </div>
          )}
          
          {deepgramError && (
            <div className="mb-4 p-4 bg-red-500/20 border border-red-500/30 rounded-lg">
              <p className="text-red-400 flex items-center gap-2">
                <AlertCircle className="w-5 h-5" />
                {deepgramError}
              </p>
            </div>
          )}

          {/* Info Box */}
          <div className="mb-6 p-4 bg-cyan-500/10 border border-cyan-500/20 rounded-lg">
            <p className="text-cyan-300 text-sm">
              <strong>🎯 Como funciona:</strong> Quando um cliente envia um áudio no WhatsApp, 
              o Deepgram transcreve automaticamente para texto e a IA responde normalmente.
            </p>
            <p className="text-cyan-200 text-xs mt-2">
              📌 Obtenha sua API Key em: <a href="https://console.deepgram.com/" target="_blank" rel="noopener noreferrer" className="underline hover:text-white">console.deepgram.com</a>
            </p>
          </div>

          <div className="space-y-4">
            {/* Toggle Habilitado */}
            <div className="flex items-center justify-between p-4 bg-gray-800/50 rounded-lg">
              <div>
                <p className="text-white font-medium">Habilitar Transcrição de Áudio</p>
                <p className="text-gray-400 text-sm">Permitir que a IA responda mensagens de voz</p>
              </div>
              <Switch
                checked={deepgramConfig.enabled}
                onCheckedChange={(checked) => setDeepgramConfig({...deepgramConfig, enabled: checked})}
              />
            </div>

            {/* API Key */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                <Key className="w-4 h-4 inline mr-2" />
                Deepgram API Key {!deepgramConfigured && '*'}
              </label>
              {deepgramConfigured && (
                <div className="mb-2 p-2 bg-gray-800/50 rounded text-gray-400 text-sm font-mono">
                  Configurada: {deepgramMaskedKey}
                </div>
              )}
              <input
                type="password"
                value={deepgramConfig.deepgram_api_key}
                onChange={(e) => setDeepgramConfig({...deepgramConfig, deepgram_api_key: e.target.value})}
                placeholder={deepgramConfigured ? "Digite nova chave para atualizar" : "Cole sua API Key do Deepgram"}
                className="w-full bg-gray-800/50 border border-gray-700 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent font-mono"
              />
            </div>

            {/* Botão Salvar */}
            <div className="pt-4">
              <button
                onClick={handleSaveDeepgram}
                disabled={savingDeepgram}
                className="w-full sm:w-auto px-6 py-3 bg-gradient-to-r from-teal-500 to-cyan-500 text-white font-semibold rounded-lg hover:from-teal-600 hover:to-cyan-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2"
              >
                {savingDeepgram ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                    Salvando...
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-5 h-5" />
                    Salvar Configuração
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Divider */}
      <div className="border-t border-gray-700 my-8"></div>

      {/* Evolution API Section */}
      <EvolutionAPIConfigSection />

      {/* Divider */}
      <div className="border-t border-gray-700 my-8"></div>

      {/* Mercado Pago Header */}
      <div>
        <h2 className="text-2xl font-bold text-white mb-2">Mercado Pago</h2>
        <p className="text-gray-400">Configurações para renovações de planos</p>
      </div>

      {/* Status Card */}
      {configured && (
        <div className="glass-card border-2 border-green-500/30 bg-gradient-to-br from-green-500/10 to-emerald-500/10">
          <div className="flex items-center gap-4 p-6">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-green-500 to-emerald-500 flex items-center justify-center">
              <CheckCircle className="w-6 h-6 text-white" />
            </div>
            <div className="flex-1">
              <h3 className="text-white font-bold text-lg">Sistema Configurado</h3>
              <p className="text-gray-400 text-sm">Mercado Pago está conectado e funcionando</p>
            </div>
          </div>
        </div>
      )}

      {/* Info Alert */}
      <div className="glass-card border-l-4 border-blue-500 bg-blue-500/10">
        <div className="p-6">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center flex-shrink-0">
              <Key className="w-5 h-5 text-blue-400" />
            </div>
            <div>
              <h3 className="text-white font-semibold mb-2">Sobre estas configurações</h3>
              <p className="text-gray-400 text-sm mb-2">
                Estas credenciais são usadas para processar <strong className="text-white">renovações de planos dos lojistas</strong>. 
                O dinheiro vai para a conta do Mercado Pago configurada aqui (conta do dono do sistema).
              </p>
              <p className="text-gray-400 text-sm">
                <strong className="text-yellow-400">Importante:</strong> Não confunda com as credenciais que cada loja configura 
                para receber pagamentos dos clientes finais.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Settings Form */}
      <div className="glass-card">
        <div className="p-6 space-y-6">
          <div>
            <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <Key className="w-5 h-5" />
              Credenciais do Mercado Pago
            </h2>

            {/* Access Token */}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Access Token (Obrigatório) *
                </label>
                {configured && !showTokens && (
                  <div className="mb-3 p-3 bg-gray-800/50 rounded-lg border border-gray-700">
                    <div className="flex items-center justify-between">
                      <span className="text-gray-400 text-sm font-mono">{maskedData.token}</span>
                      <button
                        onClick={() => setShowTokens(true)}
                        className="text-blue-400 hover:text-blue-300 text-sm flex items-center gap-1"
                      >
                        <Eye size={16} />
                        Atualizar
                      </button>
                    </div>
                  </div>
                )}
                
                {(!configured || showTokens) && (
                  <input
                    type="text"
                    value={settings.mp_access_token_global}
                    onChange={(e) => setSettings({...settings, mp_access_token_global: e.target.value})}
                    placeholder="APP_USR-XXXXX-XXXXXX-XXXXX-XXXXXX"
                    className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-blue-500 font-mono text-sm"
                  />
                )}
                <p className="text-gray-500 text-xs mt-1">
                  Token de acesso da sua conta Mercado Pago (usado para processar renovações)
                </p>
              </div>

              {/* Public Key */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Public Key (Opcional)
                </label>
                {configured && !showTokens && (
                  <div className="mb-3 p-3 bg-gray-800/50 rounded-lg border border-gray-700">
                    <div className="flex items-center justify-between">
                      <span className="text-gray-400 text-sm font-mono">{maskedData.key}</span>
                      <button
                        onClick={() => setShowTokens(true)}
                        className="text-blue-400 hover:text-blue-300 text-sm flex items-center gap-1"
                      >
                        <Eye size={16} />
                        Atualizar
                      </button>
                    </div>
                  </div>
                )}
                
                {(!configured || showTokens) && (
                  <input
                    type="text"
                    value={settings.mp_public_key_global}
                    onChange={(e) => setSettings({...settings, mp_public_key_global: e.target.value})}
                    placeholder="APP_USR-XXXXX-XXXXXX (opcional)"
                    className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-blue-500 font-mono text-sm"
                  />
                )}
                <p className="text-gray-500 text-xs mt-1">
                  Chave pública (usado para algumas integrações frontend)
                </p>
              </div>
            </div>
          </div>

          <div className="h-px bg-gray-700"></div>

          {/* WhatsApp AI Config Section */}
          <WhatsAppAIConfigSection />

          <div className="h-px bg-gray-700"></div>

          {/* Subscription Price */}
          <div>
            <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <DollarSign className="w-5 h-5" />
              Preço da Assinatura
            </h2>
            
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Valor Mensal (R$) *
              </label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400">
                  R$
                </span>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={settings.subscription_price}
                  onChange={(e) => setSettings({...settings, subscription_price: parseFloat(e.target.value)})}
                  className="w-full bg-gray-800 border border-gray-700 rounded-lg pl-12 pr-4 py-3 text-white focus:outline-none focus:border-blue-500 text-lg font-semibold"
                />
              </div>
              <p className="text-gray-500 text-xs">
                Valor cobrado mensalmente de cada loja para renovação do plano
              </p>
            </div>

            {/* URL Base do Cardápio */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-300 mb-2">
                URL Base do Cardápio (WhatsApp)
              </label>
              <input
                type="url"
                value={settings.menu_base_url}
                onChange={(e) => setSettings({...settings, menu_base_url: e.target.value})}
                placeholder="https://seudominio.com"
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-blue-500"
              />
              <p className="text-gray-500 text-xs">
                URL do domínio principal que será usada nos links do cardápio enviados via WhatsApp.
                Ex: https://zapdelivery.shop → Link: https://zapdelivery.shop/menu/nomeloja
              </p>
              {!settings.menu_base_url && (
                <p className="text-yellow-500 text-xs">
                  ⚠️ Se não configurado, será usado o domínio do ambiente atual (pode mudar em deploys)
                </p>
              )}
            </div>
          </div>

          {/* Feedback Messages */}
          {error && (
            <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
              <div className="flex items-center gap-2">
                <XCircle className="w-5 h-5 text-red-400" />
                <p className="text-red-400 font-medium">{error}</p>
              </div>
            </div>
          )}

          {success && (
            <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4">
              <div className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-green-400" />
                <p className="text-green-400 font-medium">{success}</p>
              </div>
            </div>
          )}

          {/* Save Button */}
          <div className="flex gap-3">
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex-1 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white font-bold py-4 px-6 rounded-xl transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {saving ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  Salvando...
                </>
              ) : (
                <>
                  <CheckCircle className="w-5 h-5" />
                  {configured ? 'Atualizar Configurações' : 'Salvar Configurações'}
                </>
              )}
            </button>

            {showTokens && (
              <button
                onClick={() => {
                  setShowTokens(false);
                  setSettings({
                    ...settings,
                    mp_access_token_global: '',
                    mp_public_key_global: ''
                  });
                }}
                className="px-6 py-4 bg-gray-700 hover:bg-gray-600 text-white font-semibold rounded-xl transition-all"
              >
                Cancelar
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Ferramentas de Diagnóstico - Pagamentos */}
      <div className="glass-card border border-purple-500/30 bg-purple-500/5">
        <div className="p-6">
          <h3 className="text-purple-400 font-bold mb-4 flex items-center gap-2">
            <AlertCircle className="w-5 h-5" />
            Diagnóstico de Pagamentos (Webhooks)
          </h3>
          
          <p className="text-gray-400 text-sm mb-4">
            Use estas ferramentas quando pagamentos foram feitos mas o sistema não renovou automaticamente as assinaturas.
          </p>

          <div className="flex flex-wrap gap-3 mb-4">
            <button
              onClick={loadDiagnostico}
              disabled={loadingDiagnostico}
              className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white font-medium rounded-lg transition-all disabled:opacity-50 flex items-center gap-2"
            >
              {loadingDiagnostico ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              ) : (
                <Eye className="w-4 h-4" />
              )}
              Ver Diagnóstico
            </button>
            
            <button
              onClick={handleSincronizarPagamentos}
              disabled={syncing}
              className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white font-medium rounded-lg transition-all disabled:opacity-50 flex items-center gap-2"
            >
              {syncing ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              ) : (
                <CheckCircle className="w-4 h-4" />
              )}
              Sincronizar Pagamentos Pendentes
            </button>
          </div>

          {/* Resultado do Diagnóstico */}
          {diagnostico && (
            <div className="bg-gray-800/50 rounded-lg p-4 mb-4">
              <h4 className="text-white font-semibold mb-2">Resultado do Diagnóstico</h4>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                <div className="bg-gray-700/50 rounded-lg p-3 text-center">
                  <p className="text-yellow-400 text-2xl font-bold">{diagnostico.estatisticas?.pendentes || 0}</p>
                  <p className="text-gray-400 text-xs">Pendentes</p>
                </div>
                <div className="bg-gray-700/50 rounded-lg p-3 text-center">
                  <p className="text-green-400 text-2xl font-bold">{diagnostico.estatisticas?.aprovados || 0}</p>
                  <p className="text-gray-400 text-xs">Aprovados</p>
                </div>
              </div>

              {diagnostico.verificacoes?.length > 0 && (
                <div className="mb-3">
                  <p className="text-gray-400 text-sm">Verificações:</p>
                  <ul className="text-sm space-y-1 mt-1">
                    {diagnostico.verificacoes.map((v, i) => (
                      <li key={i} className="text-gray-300">{v}</li>
                    ))}
                  </ul>
                </div>
              )}

              {diagnostico.recomendacoes?.length > 0 && (
                <div className="bg-yellow-500/10 border border-yellow-500/30 rounded p-3">
                  <p className="text-yellow-400 text-sm font-medium mb-1">Recomendações:</p>
                  <ul className="text-sm space-y-1">
                    {diagnostico.recomendacoes.map((r, i) => (
                      <li key={i} className="text-yellow-300/80">{r}</li>
                    ))}
                  </ul>
                </div>
              )}

              {diagnostico.webhook_url_esperada && (
                <div className="mt-3 bg-blue-500/10 border border-blue-500/30 rounded p-3">
                  <p className="text-blue-400 text-sm font-medium">URL do Webhook (configure no Mercado Pago):</p>
                  <code className="text-blue-300 text-xs break-all">{diagnostico.webhook_url_esperada}</code>
                </div>
              )}
            </div>
          )}

          {/* Resultado da Sincronização */}
          {syncResult && (
            <div className="bg-gray-800/50 rounded-lg p-4">
              <h4 className="text-white font-semibold mb-3">Resultado da Sincronização</h4>
              
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
                <div className="bg-gray-700/50 rounded-lg p-3 text-center">
                  <p className="text-blue-400 text-xl font-bold">{syncResult.total_verificados}</p>
                  <p className="text-gray-400 text-xs">Verificados</p>
                </div>
                <div className="bg-gray-700/50 rounded-lg p-3 text-center">
                  <p className="text-green-400 text-xl font-bold">{syncResult.aprovados}</p>
                  <p className="text-gray-400 text-xs">Aprovados</p>
                </div>
                <div className="bg-gray-700/50 rounded-lg p-3 text-center">
                  <p className="text-red-400 text-xl font-bold">{syncResult.rejeitados}</p>
                  <p className="text-gray-400 text-xs">Rejeitados</p>
                </div>
                <div className="bg-gray-700/50 rounded-lg p-3 text-center">
                  <p className="text-orange-400 text-xl font-bold">{syncResult.cancelados}</p>
                  <p className="text-gray-400 text-xs">Cancelados</p>
                </div>
                <div className="bg-gray-700/50 rounded-lg p-3 text-center">
                  <p className="text-yellow-400 text-xl font-bold">{syncResult.ainda_pendentes}</p>
                  <p className="text-gray-400 text-xs">Pendentes</p>
                </div>
              </div>

              {syncResult.aprovados > 0 && (
                <div className="bg-green-500/10 border border-green-500/30 rounded p-3">
                  <p className="text-green-400 text-sm">
                    ✅ {syncResult.aprovados} assinatura(s) foram renovadas automaticamente!
                  </p>
                </div>
              )}

              {syncResult.detalhes?.length > 0 && (
                <details className="mt-3">
                  <summary className="text-gray-400 text-sm cursor-pointer hover:text-white">
                    Ver detalhes ({syncResult.detalhes.length} itens)
                  </summary>
                  <div className="mt-2 max-h-48 overflow-y-auto">
                    {syncResult.detalhes.map((d, i) => (
                      <div key={i} className="text-xs text-gray-400 border-b border-gray-700 py-2">
                        <span className="text-white">#{d.payment_id}</span> - {d.status_novo || d.erro}
                        {d.acao && <span className="text-green-400 ml-2">→ {d.acao}</span>}
                      </div>
                    ))}
                  </div>
                </details>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Help Card */}
      <div className="glass-card border border-yellow-500/30 bg-yellow-500/5">
        <div className="p-6">
          <h3 className="text-yellow-400 font-bold mb-3 flex items-center gap-2">
            <AlertTriangle className="w-5 h-5" />
            Como obter as credenciais do Mercado Pago?
          </h3>
          <ol className="text-gray-400 text-sm space-y-2 list-decimal list-inside">
            <li>Acesse sua conta Mercado Pago</li>
            <li>Vá em <strong className="text-white">Seu negócio → Configurações → Credenciais</strong></li>
            <li>Escolha <strong className="text-white">Credenciais de produção</strong></li>
            <li>Copie o <strong className="text-white">Access Token</strong> e a <strong className="text-white">Public Key</strong></li>
            <li>Cole aqui e salve</li>
          </ol>
          <p className="text-yellow-400 text-sm mt-4">
            ⚠️ Mantenha essas credenciais seguras e nunca compartilhe com terceiros!
          </p>
        </div>
      </div>
    </div>
  );
};


// ============================================================
// GALERIA DE IMAGENS - MASTER
// ============================================================

